import { useState, useEffect } from "react";
import { useNavigate, createFileRoute } from "@tanstack/react-router";
import { Plus, Pencil, Trash2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { PageContainer } from "@/components/shared/page-container";
import { Header } from "@/components/shared/header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAlertDialogStore } from "@/lib/store/use-alert-dialog-store";
import {
  useCompetitionGroups,
  useCreateCompetitionGroup,
  useUpdateCompetitionGroup,
  useDeleteCompetitionGroup,
} from "@/lib/api/competition/hooks";
import { useLoadingIndicator } from "@/lib/hooks/use-loading-indicator";
import type { CompetitionGroup } from "@/lib/api/competition/types";

function CompetitionGroupsPage() {
  const navigate = useNavigate();
  const { setAlertDialogState, resetAlertDialogState } = useAlertDialogStore();

  // 현재 사용자의 officeId와 role
  const [officeId, setOfficeId] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem("growing-teacher-user");
    if (stored) {
      try {
        const user = JSON.parse(stored);
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setOfficeId(user.office?._id || user.office);
        setUserRole(user.role);
      } catch {
        // ignore
      }
    }
  }, []);

  const isAdmin = userRole === "admin";

  // 그룹 목록
  const { data: groups, isLoading } = useCompetitionGroups(
    officeId ?? undefined,
  );
  useLoadingIndicator(isLoading);

  // Mutations
  const createGroup = useCreateCompetitionGroup();
  const updateGroup = useUpdateCompetitionGroup();
  const deleteGroup = useDeleteCompetitionGroup();

  // 다이얼로그 상태
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<CompetitionGroup | null>(
    null,
  );
  const [groupName, setGroupName] = useState("");

  const handleOpenCreate = () => {
    setEditingGroup(null);
    setGroupName("");
    setIsDialogOpen(true);
  };

  const handleOpenEdit = (group: CompetitionGroup) => {
    setEditingGroup(group);
    setGroupName(group.name);
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!groupName.trim()) {
      toast.error("그룹 이름을 입력하세요.");
      return;
    }

    try {
      if (editingGroup) {
        await updateGroup.mutateAsync({
          id: editingGroup._id,
          data: { name: groupName.trim() },
        });
        toast.success("그룹이 수정되었습니다.");
      } else {
        await createGroup.mutateAsync({ name: groupName.trim() });
        toast.success("그룹이 생성되었습니다.");
      }
      setIsDialogOpen(false);
      setGroupName("");
      setEditingGroup(null);
    } catch (error) {
      const axiosError = error as {
        response?: { data?: { message?: string } };
      };
      toast.error(axiosError.response?.data?.message || "작업에 실패했습니다.");
    }
  };

  const handleDelete = (group: CompetitionGroup) => {
    setAlertDialogState({
      title: "그룹 삭제",
      description: `"${group.name}" 그룹을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`,
      confirmText: "삭제",
      onConfirm: async () => {
        resetAlertDialogState();
        try {
          await deleteGroup.mutateAsync(group._id);
          toast.success("그룹이 삭제되었습니다.");
        } catch (error) {
          const axiosError = error as {
            response?: { data?: { message?: string } };
          };
          toast.error(
            axiosError.response?.data?.message || "삭제에 실패했습니다.",
          );
        }
      },
      onCancel: () => {
        resetAlertDialogState();
      },
    });
  };

  // admin이 아니면 접근 불가
  if (userRole && !isAdmin) {
    return (
      <PageContainer>
        <div className="flex flex-col items-center justify-center py-16">
          <p className="text-muted-foreground mb-4">
            그룹 관리는 관리자만 접근할 수 있습니다.
          </p>
          <Button variant="outline" onClick={() => navigate({ to: "/competition" })}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            돌아가기
          </Button>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header
        title="Competition 그룹 관리"
        description="Competition에서 사용할 그룹을 관리합니다."
        left={
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate({ to: "/competition" })}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
        }
        right={
          <Button onClick={handleOpenCreate}>
            <Plus className="h-4 w-4 mr-2" />
            그룹 추가
          </Button>
        }
      />

      {/* 그룹 목록 테이블 */}
      {!groups?.length ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <p className="text-muted-foreground mb-4">등록된 그룹이 없습니다.</p>
          <Button onClick={handleOpenCreate}>
            <Plus className="h-4 w-4 mr-2" />
            그룹 추가
          </Button>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>그룹 이름</TableHead>
              <TableHead>지점</TableHead>
              <TableHead>생성일</TableHead>
              <TableHead className="w-24">작업</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.map((group) => (
              <TableRow key={group._id}>
                <TableCell className="font-medium">{group.name}</TableCell>
                <TableCell>{group.office.name}</TableCell>
                <TableCell>
                  {new Date(group.createdAt).toLocaleDateString("ko-KR")}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleOpenEdit(group)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(group)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* 생성/수정 다이얼로그 */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingGroup ? "그룹 수정" : "그룹 추가"}
            </DialogTitle>
            <DialogDescription>
              Competition에서 사용할 그룹 이름을 입력하세요.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="groupName">그룹 이름</Label>
              <Input
                id="groupName"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="예: 초등 고급반"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSubmit();
                  }
                }}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              취소
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={createGroup.isPending || updateGroup.isPending}
            >
              {createGroup.isPending || updateGroup.isPending
                ? "저장 중..."
                : editingGroup
                  ? "수정"
                  : "추가"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}

export const Route = createFileRoute("/_content/competition/groups")({
  component: CompetitionGroupsPage,
});
